import { ArrowUpRight, ArrowDownRight, Building2 } from 'lucide-react';
import type { MercuryAccount } from '@/lib/mercuryService';

function formatBalanceValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface MercuryAccountCardProps {
  account: MercuryAccount;
  isSelected: boolean;
  onClick: () => void;
}

export default function MercuryAccountCard({ account, isSelected, onClick }: MercuryAccountCardProps) {
  const isPositive = account.currentBalance > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-2xl p-5 transition-all duration-300 ${
        isSelected
          ? 'bg-sky-600 shadow-lg shadow-sky-600/20 scale-[1.02]'
          : 'glass-panel hover:shadow-lg hover:-translate-y-0.5'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isSelected ? 'bg-white/20' : 'bg-white/10'
          }`}>
            <Building2 size={18} className={isSelected ? 'text-white' : 'text-white'} />
          </div>
          <div>
            <p className={`text-xs font-medium ${isSelected ? 'text-white/70' : 'text-white'}`}>
              {account.name || 'Account'}
            </p>
            <p className={`text-[11px] ${isSelected ? 'text-white/50' : 'text-white'}`}>
              {account.kind === 'checking' ? 'Checking' : account.kind === 'savings' ? 'Savings' : account.kind}
            </p>
          </div>
        </div>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
          isSelected
            ? 'bg-white/20'
            : isPositive ? 'bg-emerald-500/10' : 'bg-red-500/10'
        }`}>
          {isPositive
            ? <ArrowUpRight size={14} className={isSelected ? 'text-white' : 'text-emerald-800'} />
            : <ArrowDownRight size={14} className={isSelected ? 'text-white' : 'text-red-600'} />
          }
        </div>
      </div>

      <p className={`text-xl font-bold tracking-tight tabular-nums ${
        isSelected ? 'text-white' : 'text-white'
      }`}>
        {formatBalanceValue(account.currentBalance)}
      </p>

      {account.availableBalance !== account.currentBalance && (
        <p className={`text-[11px] mt-1 ${isSelected ? 'text-white/60' : 'text-white'}`}>
          Available: {formatBalanceValue(account.availableBalance)}
        </p>
      )}
    </button>
  );
}
